import type { HandlingState } from '@shared/types';
import { cn } from '@web/lib/cn';

/**
 * Who is holding this conversation.
 *
 * The four states differ in SHAPE before they differ in hue: the border
 * treatment, the marker geometry and the left bar are all distinct, so the chip
 * survives a colour-blind reader, a bad monitor, and — the case that actually
 * matters — a glance from two metres away. The label is always present; a bare
 * coloured dot would fail WCAG 1.4.1 and, more to the point, would be unreadable
 * at the density this board runs at.
 *
 * `parked` is the only state that earns the reserved signal hue, because it is
 * the only one that means "a human is needed now".
 */

const LABEL: Record<HandlingState, string> = {
  ai: 'AI',
  parked: 'PARKED',
  human: 'HUMAN',
  resolved: 'DONE',
};

const DESCRIPTION: Record<HandlingState, string> = {
  ai: 'handled by the assistant',
  parked: 'waiting for a human',
  human: 'held by a colleague',
  resolved: 'resolved',
};

/** Marker geometry — the part that carries meaning without any colour at all. */
function Marker({ state }: { state: HandlingState }) {
  const base = 'inline-block shrink-0';
  switch (state) {
    case 'ai':
      // Hollow square: nothing is being held.
      return <span aria-hidden className={cn(base, 'size-[6px] border border-current')} />;
    case 'parked':
      // Filled square: something is sitting there.
      return <span aria-hidden className={cn(base, 'size-[6px] bg-current')} />;
    case 'human':
      // Diamond: a person turned it.
      return <span aria-hidden className={cn(base, 'size-[6px] rotate-45 bg-current')} />;
    case 'resolved':
      // Bar: closed out.
      return <span aria-hidden className={cn(base, 'h-px w-[8px] bg-current')} />;
  }
}

export function StateChip({ state, className }: { state: HandlingState; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex h-5 items-center gap-1.5 px-1.5 font-mono text-[10px] leading-none font-medium tracking-[0.08em] whitespace-nowrap',
        state === 'ai' && 'border border-dashed border-ink-faint text-ink-faint',
        state === 'parked' &&
          'border border-signal border-l-[3px] bg-signal/12 text-signal dark:bg-signal/16',
        state === 'human' && 'border border-ink-dim bg-ink-dim/20 text-ink',
        state === 'resolved' && 'border border-dotted border-ink-faint text-ink-faint opacity-80',
        className,
      )}
    >
      <Marker state={state} />
      {LABEL[state]}
      <span className="sr-only"> — {DESCRIPTION[state]}</span>
    </span>
  );
}
